import { useMemo, useState } from 'react';
import {
  Divider,
  ScrollArea
} from '@mantine/core';
import { useParams } from 'react-router-dom';

import { SHAREPOINT_INTEGRATION_TYPE } from '../../../utils/apiUtils';
import CredentialsForm from './CredentialsForm';
import SyncConfigsForm from './SyncConfigsForm';
import useOrgPermissions from '../../../hooks/useOrgPermissions';
import useEnsisQuery from '../../../hooks/useEnsisQuery';
import { CenteredLoader } from '../../../components';

const STORAGE_PROVIDER_REFETCH_INTERVAL_MS = 10000;

const SharePointSettings: React.FC = () => {
  const { organization } = useParams();
  const { userHasPermission } = useOrgPermissions();
  const [refetchEnabled, setRefetchEnabled] = useState(false);

  const canEdit = userHasPermission('change');

  const { data, isLoading } = useEnsisQuery(
    '/app/storage-providers',
    {
      queryParams: { organization_slug: organization ?? '' },
      refetchInterval: refetchEnabled ? STORAGE_PROVIDER_REFETCH_INTERVAL_MS : undefined
    }
  );

  const shouldEnableRefetch = (data ?? []).some((provider) => provider.is_syncing);
  if (shouldEnableRefetch !== refetchEnabled) {
    setRefetchEnabled(shouldEnableRefetch);
  }

  const sharepointStorageConfig = useMemo(() => (
    data?.find((storageConfig) => storageConfig.type === SHAREPOINT_INTEGRATION_TYPE)
  ), [data]);

  if (isLoading) {
    return <CenteredLoader />;
  }

  return (
    <ScrollArea w='100%' type='hover' style={{ height: 'calc(100vh - 250px)' }}>
      <CredentialsForm
        mb={16}
        storageProviderConfig={sharepointStorageConfig}
        canEdit={canEdit}
      />
      {
        sharepointStorageConfig !== undefined && (
          <>
            <Divider mb={16} />
            <SyncConfigsForm
              maw={800}
              mb={32}
              canEdit={canEdit}
              storageProviderConfig={sharepointStorageConfig}
            />
          </>
        )
      }
    </ScrollArea>
  );
};

export default SharePointSettings;
